import { useState } from 'react'
import { createFileRoute, useNavigate, useSearch, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { supabase } from '@/integrations/supabase/client'
import { LogoMark } from '@/components/skl/Logo'

const searchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/auth')({
  validateSearch: searchSchema,
  component: AuthPage,
  head: () => ({
    meta: [
      { title: 'Log ind — SKL-Flow' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
})

function AuthPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/auth' })
  const [mode, setMode] = useState<'login' | 'signup'>('login')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)


  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (mode === 'signup') {
      if (!firstName.trim() || !lastName.trim() || !company.trim()) {
        setError('Udfyld venligst alle felter.')
        return
      }
      if (password !== passwordConfirm) {
        setError('Adgangskoderne matcher ikke.')
        return
      }
    }

    setLoading(true)
    try {
      if (mode === 'signup') {
        const fullName = `${firstName.trim()} ${lastName.trim()}`.trim()
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              full_name: fullName,
              navn: fullName,
              virksomhed: company.trim(),
            },
          },
        })
        if (error) throw error
        // Supabase returns a user with empty identities when the email is already registered.
        if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
          throw new Error('Der findes allerede en konto med denne e-mail. Log venligst ind i stedet.')
        }
        // Bekræftelsesmail er sendt — brugeren skal klikke linket før login.
        setPendingEmail(email)
      } else {

        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
        const dest =
          typeof search.redirect === 'string' && search.redirect.startsWith('/')
            ? search.redirect
            : '/'
        navigate({ to: dest })
      }

    } catch (err: any) {
      setError(err?.message ?? 'Noget gik galt')
    } finally {
      setLoading(false)
    }
  }

  const fieldClass =
    'w-full rounded-xl border border-foreground/10 bg-background/40 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/50'
  const labelClass =
    'block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1'

  if (pendingEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-md rounded-3xl border border-foreground/10 bg-card/60 backdrop-blur-xl p-8 shadow-2xl text-center">
          <Link to="/" className="flex items-center justify-center gap-2 mb-6">
            <LogoMark className="h-8 w-8" />
            <span className="font-display font-bold text-lg tracking-tight">SKL-Flow</span>
          </Link>
          <div className="mx-auto h-14 w-14 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-3xl">
            ✉
          </div>
          <h1 className="mt-5 font-display text-2xl font-bold">Bekræft din e-mail</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Vi har sendt en bekræftelsesmail til{' '}
            <span className="font-semibold text-foreground">{pendingEmail}</span>. Åbn mailen og
            klik på linket for at aktivere din konto, før du kan logge ind.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            Tjek også din spam-mappe, hvis du ikke kan finde mailen.
          </p>
          <button
            type="button"
            onClick={() => {
              setPendingEmail(null)
              setMode('login')
              setPassword('')
              setPasswordConfirm('')
            }}
            className="mt-6 w-full rounded-xl bg-primary text-primary-foreground font-bold px-6 py-3 hover:bg-primary/90 transition"
          >
            Gå til login
          </button>
          <Link
            to="/"
            className="mt-4 inline-block text-xs text-muted-foreground hover:text-foreground transition"
          >
            ← Tilbage til forsiden
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md rounded-3xl border border-foreground/10 bg-card/60 backdrop-blur-xl p-8 shadow-2xl">

        <Link to="/" className="flex items-center gap-2 mb-6">
          <LogoMark className="h-8 w-8" />
          <span className="font-display font-bold text-lg tracking-tight">SKL-Flow</span>
        </Link>
        <h1 className="font-display text-2xl font-bold mb-1">
          {mode === 'login' ? 'Log ind' : 'Opret konto'}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {mode === 'login'
            ? 'Log ind for at tilgå administrationen.'
            : 'Opret en konto for at komme videre.'}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="first-name">Navn</label>

                  <input
                    id="first-name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={fieldClass}
                    placeholder="Fornavn"
                    autoComplete="given-name"
                    maxLength={80}
                  />
                </div>
                <div>
                  <label className={labelClass}>Efternavn</label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={fieldClass}
                    placeholder="Efternavn"
                    autoComplete="family-name"
                    maxLength={80}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Virksomhedsnavn</label>
                <input
                  type="text"
                  required
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className={fieldClass}
                  placeholder="Fx Hansens VVS"
                  autoComplete="organization"
                  maxLength={160}
                />
              </div>
            </>
          )}

          <div>
            <label className={labelClass}>E-mail</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={fieldClass}
              placeholder="dig@firma.dk"
              autoComplete="email"
            />
          </div>
          <div>
            <label className={labelClass}>Adgangskode</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={fieldClass}
              placeholder="Mindst 6 tegn"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>

          {mode === 'signup' && (
            <div>
              <label className={labelClass}>Gentag adgangskode</label>
              <input
                type="password"
                required
                minLength={6}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className={fieldClass}
                placeholder="Gentag din adgangskode"
                autoComplete="new-password"
              />
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary text-primary-foreground font-bold px-6 py-3 shadow-[0_0_30px_-8px_color-mix(in_oklab,var(--primary)_70%,transparent)] hover:bg-primary/90 disabled:opacity-60 transition"
          >
            {loading ? 'Vent…' : mode === 'login' ? 'Log ind' : 'Opret konto'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          {mode === 'login' ? (
            <>
              Har du ikke en konto?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signup')
                  setError(null)
                }}
                className="text-accent font-semibold hover:underline"
              >
                Opret konto
              </button>
            </>
          ) : (
            <>
              Har du allerede en konto?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('login')
                  setError(null)
                }}
                className="text-accent font-semibold hover:underline"
              >
                Log ind
              </button>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition">
            ← Tilbage til forsiden
          </Link>
        </div>
      </div>
    </div>
  )
}
